"use client";

import Image from "next/image";
import { useState, useEffect, useMemo } from "react";
import Skeleton from "react-loading-skeleton";
import styles from "./index.module.css";
import StoreBox from "../store/store-box";
import { useHistoryManager } from "../history/useHistoryManager";
import { useClient, useToken } from "../../lib/context/auth-context";
import {
  setAccessPoint,
  setAccessPointId,
  setAccessPoints,
  setOrganisation,
} from "../slice/accessOrg-slice";
import { useAppDispatch } from "../slice/hooks";
import {
  useFetchAccessPoint,
  useFetchMainAccessPoint,
  useOrganisationCategories,
} from "../../lib/util";
import OrganisationCategories from "./org-categories";
import { updateFullPath } from "../slice/path-slice";
import SkeletonCard from "../skeletoncard/skeletoncard";
import Pagination from "../common/Pagination";
import { useQuery } from "@tanstack/react-query";

const MarketplaceSkeleton = () => {
  return (
    <div style={{ width: "100%" }}>
      <div style={{ marginBottom: "2rem" }}>
        <Skeleton height={400} width="100%" />
      </div>

      {Array.from({ length: 3 }).map((_, idx) => (
        <div style={{ width: "100%" }} key={idx}>
          <div style={{ margin: "0 auto", maxWidth: "1200px" }}>
            <SkeletonCard />
          </div>
        </div>
      ))}
    </div>
  );
};

const MarketPlace = (props) => {
  const { urlData, serverToken } = props;

  const { token } = useToken();
  const dispatch = useAppDispatch();
  const { navigateTo } = useHistoryManager();
  const client = useClient();

  const [orgState, setOrgState] = useState({
    organisations: [],
    limit: 10,
    total: 0,
    currentPage: 1,
    page: 1,
    search: "phone",
    ...urlData,
  });

  useEffect(() => {
    const defaultPath = {
      storePath: "market-place",
      basePath: "market-place",
    };
    dispatch(updateFullPath(defaultPath));
    dispatch(setAccessPointId(null));
    dispatch(setAccessPoint(null));
    dispatch(setAccessPoints([]));
    dispatch(setOrganisation(null));
  }, []);

  const orgQueryKey = useMemo(() => ["feeds", orgState.limit], [orgState.page]);
  const {
    data: feeds,
    isLoading,
    isFetching,
    error,
  } = useQuery(
    orgQueryKey,
    async () => {
      const queryParams = [
        `limit=${orgState.limit}`,
        `page=${orgState.page}`,
        `searchTerm=${orgState.search}`,
      ];
      const queryString = `?${queryParams.join("&")}`;

      const res = await client(
        `organisation/organisations/storefront/enabled${queryString}`,
        {
          headers: {
            Authorization: `Bearer ${token || serverToken}`,
          },
        }
      );
      return res.data;
    },
    {
      enabled: Boolean(token || serverToken),
      keepPreviousData: true,
    }
  );

  const [imageLoadedMap, setImageLoadedMap] = useState({});

  const [newOrganisation, setNewOrganisation] = useState(null);
  const [switching, setSwitching] = useState(false);

  const { mutateAsync: fetchAccessPoint } = useFetchAccessPoint({
    onError: (error) => {
      console.error("Failed to fetch access point:", error);
      setSwitching(false);
    },
    onSuccess: (response) => {
      const accessPoints = response.data.accessPoints;

      const newPath = {
        storePath: `/store/${newOrganisation.handle}${
          newOrganisation.location ? `/${newOrganisation.location}` : ""
        }`,
        basePath: `/store/${newOrganisation.handle}`,
      };

      navigateTo(
        `store/${newOrganisation.handle}`,
        {
          // replace: true,
          onNavigate: (navigate) => {
            setSwitching(false);
            if (navigate) {
              dispatch(updateFullPath(newPath));
              dispatch(setOrganisation(newOrganisation));
              if (accessPoints && accessPoints.length > 0) {
                dispatch(setAccessPointId(accessPoints[0].accessPointId));
                dispatch(setAccessPoint(accessPoints[0]));
                dispatch(setAccessPoints(accessPoints));
              } else {
                console.log(
                  "No access points found for:",
                  newOrganisation.orgName
                );
              }
              if (typeof window !== "undefined") {
                window.scrollTo({
                  top: 0,
                  behavior: "smooth",
                });
              }
            }
          },
        },
        false
      );
    },
  });

  const handleSwitch = async (organisation) => {
    setNewOrganisation(organisation);
    setSwitching(true);
    fetchAccessPoint({
      orgId: organisation.orgId,
      token,
    });
  };

  const handleImageLoad = (orgId) => {
    setImageLoadedMap((prev) => ({ ...prev, [orgId]: true }));
  };

  const openPage = (page) => {
    setOrgState((prev) => ({
      ...prev,
      page,
    }));
  };

  useEffect(() => {
    if (feeds && feeds.organisations) {
      const { organisations, limit, total, currentPage } = feeds;

      setOrgState((prev) => {
        const all = [...(prev.organisations || []), ...organisations];
        const unique = all.filter(
          (org, index, self) =>
            index === self.findIndex((o) => o.orgId === org.orgId)
        );
        return { ...prev, organisations: unique, limit, total, currentPage };
      });
    }
  }, [feeds]);

  return (
    <StoreBox {...props}>
      <div className={styles.marketPlaceContainer}>
        {switching ? (
          <MarketplaceSkeleton />
        ) : (
          <>
            <div className={styles.marketPlaceHeader}>
              <h1>Marketplace</h1>
              <p>Choose an organisation to explore their store</p>
            </div>

            {orgState.organisations.length > 0 ? (
              <>
                <div className={styles.marketPlaceOrganisations}>
                  {orgState.organisations.map((org) => {
                    const isLoaded = imageLoadedMap[org.orgId];
                    return (
                      <div
                        key={org.orgId}
                        className={styles.organisation}
                        onClick={() => handleSwitch(org)}
                      >
                        <div className={styles.organisationInfo}>
                          {!org.logoUrl && (
                            <div className={styles.organisationPlaceholderLogo}>
                              {org.orgName?.[0]?.toUpperCase() || "?"}
                            </div>
                          )}
                          {org.logoUrl && (
                            <div>
                              {!isLoaded && (
                                <Skeleton width={70} height={70} circle />
                              )}
                              {isLoaded && (
                                <Image
                                  src={org.logoUrl}
                                  alt={`${org.orgName} logo`}
                                  width={70}
                                  height={70}
                                  className={styles.organisationLogo}
                                  style={{
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                  }}
                                />
                              )}
                              <img
                                src={org.logoUrl}
                                alt={org.orgName}
                                style={{ display: "none" }}
                                onLoad={() => handleImageLoad(org.orgId)}
                              />
                            </div>
                          )}
                          <div className={styles.textInfo}>
                            <p className={styles.organisationName}>
                              {org.orgName
                                .split(" ")
                                .map(
                                  (word) =>
                                    word.charAt(0).toUpperCase() +
                                    word.slice(1).toLowerCase()
                                )
                                .join(" ")}
                            </p>
                            <p className={styles.organisationDescription}>
                              {org.description || "No description available."}
                            </p>
                          </div>
                        </div>
                        <div className={styles.organisationCategories}>
                          <OrganisationCategories
                            org={org}
                            token={token}
                            handleSwitch={handleSwitch}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <Pagination
                  infiniteScroll={true}
                  total={orgState.total}
                  limit={orgState.limit}
                  page={orgState.page}
                  onPageClick={openPage}
                  enableManualMode={true}
                  autoLoadThreshold={5}
                  isLoading={isLoading}
                  isFetching={isFetching}
                  showEndOfList={true}
                  endOfListText="🎉 You've reached the end!"
                />
              </>
            ) : (
              // <-- NEW: show a loading skeleton matching the structure when organisations is empty
              <div className={styles.marketPlaceOrganisations}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className={styles.organisation}>
                    <div className={styles.organisationInfo}>
                      <div style={{ marginRight: "1rem", flexShrink: 0 }}>
                        <Skeleton width={70} height={70} circle />
                      </div>

                      <div
                        className={styles.textInfo}
                        style={{ width: "100%" }}
                      >
                        <div style={{ marginBottom: 8 }}>
                          <Skeleton width={200} height={24} />
                        </div>
                        <div>
                          <Skeleton width={300} height={18} />
                        </div>
                      </div>
                    </div>
                    <div
                      className={styles.organisationCategories}
                      aria-hidden
                      style={{ paddingLeft: "3rem" }}
                    >
                      {Array.from({ length: 5 }).map((_, j) => (
                        <div
                          key={j}
                          style={{
                            width: "30rem",
                            height: "25rem",
                            borderRadius: 7,
                            background: "#f0f0f0",
                            flex: "0 0 30rem",
                            marginRight: "1rem",
                          }}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </StoreBox>
  );
};

export default MarketPlace;
